import { useEffect, useState, useContext, useCallback } from "react";
import SendAuthorizationCodeMessage from "../../utils/send-message/send-message";
import { authorizationService } from "@/services/continente-credentials/common/AuthorizationService";
import { useAuthorizeQueryParams } from "../../hooks/continente-credentials/useAuthorizeQueryParams";
import { testpageService } from "@/services/TestPageService";
import useGetDigitalAssetId from "../../hooks/useGetDigitalAssetId";
import LoadingLogo from "@/components/loading-logo/loading-logo";
import { DigitalAssetContext } from "../../contexts/continente-credentials/DigitalAssetContext";
import { useLocation } from "react-router-dom";
import { ValidatePasswordResponse } from "@/models/continente-credentials/mobile/ValidatePasswordResponse";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/providers/theme-provider";
import { Config } from "@/config/Config";
import { tryCatch } from "@/utils/try-catch";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

export default function SignIn() {
  const { t } = useTranslation();
  const authorizeQueryParams = useAuthorizeQueryParams();
  const [authorizationCode, setAuthorizationCode] = useState("");
  const [codeVerifier, setCodeVerifier] = useState("");
  const [deeplinkAction, setDeeplinkAction] = useState("");
  const digitalAssetId = useGetDigitalAssetId();
  const { handleResponse } = useApiResponseHandler();

  const digitalAssetContext = useContext(DigitalAssetContext);
  const location = useLocation<ValidatePasswordResponse>();
  const { theme } = useTheme();

  const handleAuthorize = useCallback(async () => {
    const { data: response, error } = await tryCatch(authorizationService.authorize({
      clientId: authorizeQueryParams.clientId || "",
    }));

    if (error || !response) {
      return;
    }

    handleResponse(response, {
      onSuccess: (response) => {
        setAuthorizationCode(response.value.authorizationCode);
        setCodeVerifier(response.value.codeVerifier);

        document.getElementById("success__label")?.focus();

        SendAuthorizationCodeMessage({
          type: "success",
          authorization_code: response.value.authorizationCode,
          code_verifier: response.value.codeVerifier,
        });
      }
    }, error);
  }, [])

  useEffect(() => {
    const returnUrl = location.state?.properties?.returnUrl;

    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      handleAuthorize();
    }
  }, [authorizeQueryParams.clientId, location.state]);

  const handleOpenSiteCredentialsClick = async () => {
    if (!digitalAssetContext.digitalAsset.showSiteCredentialsLink) return;

    if (authorizationCode === "" || codeVerifier === "") {
      alert(
        "Authorization Code ou Code Verifier não estavam prontos. Tente Novamente"
      );
    } else {

      const { data: response, error } = await tryCatch(testpageService.getSiteCredentialsUrl(
        authorizationCode,
        codeVerifier,
        digitalAssetId || ""
      ));

      if (error || !response) {
        return;
      }

      handleResponse(response, {
        onSuccess: (response) => {
          const clientId = digitalAssetId ?? "";
          const token = response.value?.siteCredenciaisToken ?? "";
          window.location.href = `/site-credentials/authorize?clientId=${clientId}&token=${token}&theme=${theme}&deeplinkAction=${deeplinkAction}`;
        }
      }, error);
    }
  };

  return (
    <div className="w-full h-[100vh]">
      {digitalAssetContext.digitalAsset.showSiteCredentialsLink && (
        <div className="flex justify-center">
          <select name="category" value={deeplinkAction} onChange={event => setDeeplinkAction(event.target.value)}>
            <option value={""}>NULL</option>
            <option value={Config.DeepLinkActions.sc_home}>Home</option>
            <option value={Config.DeepLinkActions.sc_email_definition}>email_definition</option>
            <option value={Config.DeepLinkActions.sc_email_update}>email_update</option>
            <option value={Config.DeepLinkActions.sc_phoneNumber_definition}>phoneNumber_definition</option>
            <option value={Config.DeepLinkActions.sc_phoneNumber_update}>phoneNumber_update</option>
            <option value={Config.DeepLinkActions.sc_password_definition}>password_definition</option>
            <option value={Config.DeepLinkActions.sc_password_update}>password_update</option>
            <option value={Config.DeepLinkActions.sc_loyaltyCard_definition}>loyaltyCard_definition</option>
            <option value={Config.DeepLinkActions.sc_faqs}>faqs</option>
          </select>
          <span
            onClick={() => { handleOpenSiteCredentialsClick() }}
            className="text-[var(--on-surface-variant)] hover:underline cursor-pointer font-bold"
            style={{
              zIndex: 10000,
              position: "absolute",
              marginTop: "100px",
            }}
          >
            Abrir Site Credenciais
          </span>
        </div>
      )
      }
      <div
        id="success__label"
        tabIndex={10}
        aria-label={t("accessibility.deadend.success_message")}
        aria-live="assertive"
        role="alert">
      </div>
      <LoadingLogo />
    </div >
  );
}
